import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { SlideContainer } from '../components/presentation/SlideContainer'
import { IconSolid, Logo } from '../components/ui'
import type { PresentationData } from '../types'
import { loadPresentationBySlug, presentationsBySlug } from '../presentations'

export interface PresentationViewerProps {
  presentations?: Record<string, PresentationData>
}

/**
 * Loading skeleton for the presentation viewer
 */
const LoadingSkeleton: React.FC = () => (
  <div className="w-full h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse mx-auto mb-4" />
      <div className="h-6 w-48 bg-gray-200 dark:bg-gray-800 rounded animate-pulse mx-auto" />
    </div>
  </div>
)

/**
 * Presentation Viewer Page
 *
 * Displays a single presentation with full navigation and keyboard controls.
 * Fetches the presentation from the backend API (if enabled) with fallback to static presentations.
 * Implements role-based access control with proper 403 error handling.
 */
export const PresentationViewer: React.FC<PresentationViewerProps> = ({ presentations: initialPresentations }) => {
  const { id: slug } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Hide Chat widget when presentation viewer is mounted
  useEffect(() => {
    // Hide Chat widget
    const chatWidgetButton = document.querySelector('button[aria-label="Toggle chat"]')
    if (chatWidgetButton) {
      ;(chatWidgetButton as HTMLElement).style.display = 'none'
    }

    // Also hide by class if the aria-label changes
    const chatWidgets = document.querySelectorAll('[style*="background: linear-gradient(135deg, rgb(59, 130, 246)"][class*="fixed"]')
    chatWidgets.forEach(widget => {
      ;(widget as HTMLElement).style.display = 'none'
    })

    // Cleanup: restore visibility when unmounting
    return () => {
      if (chatWidgetButton) {
        ;(chatWidgetButton as HTMLElement).style.display = ''
      }
      chatWidgets.forEach(widget => {
        ;(widget as HTMLElement).style.display = ''
      })
    }
  }, [])

  const [presentation, setPresentation] = useState<PresentationData | null>(() => {
    // Try to get from initial presentations prop first (backward compatibility)
    if (slug && initialPresentations?.[slug]) {
      return initialPresentations[slug]
    }
    // Try to get from static presentations
    if (slug && presentationsBySlug[slug]) {
      return presentationsBySlug[slug]
    }
    return null
  })
  const [loading, setLoading] = useState(!presentation)
  const [error, setError] = useState<string | null>(null)
  const [isAccessDenied, setIsAccessDenied] = useState(false)

  /**
   * Load presentation data from backend or static
   */
  const loadPresentationData = useCallback(async () => {
    if (!slug) {
      setError('No slug provided')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setIsAccessDenied(false)

    try {
      const data = await loadPresentationBySlug(slug)

      if (data) {
        setPresentation(data)
      } else {
        setError('Presentation not found')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'

      // Check if this is an access denied error (403)
      if (errorMessage.includes('FORBIDDEN') || errorMessage.includes('permission') || errorMessage.includes('access')) {
        setIsAccessDenied(true)
        setError('You do not have permission to view this presentation')
      } else {
        setError(errorMessage)
      }

      console.error('Failed to load presentation:', err)
    } finally {
      setLoading(false)
    }
  }, [slug])

  /**
   * Load presentation on mount if not already loaded
   */
  useEffect(() => {
    if (!presentation && slug) {
      loadPresentationData()
    }
  }, [presentation, slug, loadPresentationData])

  /**
   * Handle back to library navigation
   */
  const handleBackToLibrary = () => {
    navigate('/presentations')
  }

  // Show loading skeleton
  if (loading) {
    return <LoadingSkeleton />
  }

  // Show access denied state (403)
  if (isAccessDenied) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center max-w-md px-6">
          <div className="w-24 h-24 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <IconSolid name="lock-closed" size="3xl" className="text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You don't have permission to view this presentation. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={handleBackToLibrary}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <IconSolid name="arrow-left" />
            Back to Library
          </button>
        </div>
      </div>
    )
  }

  // Show error state (404 or other errors)
  if (error || !presentation) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <IconSolid name="exclamation-triangle" size="3xl" className="text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Presentation Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The requested presentation could not be loaded.
          </p>
          <button
            onClick={handleBackToLibrary}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <IconSolid name="arrow-left" />
            Back to Library
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-screen relative">
      {/* Logo in top left - no text, just the icon */}
      <div className="fixed top-4 left-4 z-50">
        <div className="px-3 py-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg shadow-lg">
          <Logo size="sm" variant="color" showText={false} />
        </div>
      </div>

      {/* Presentation Container with custom navigation */}
      <SlideContainer
        presentation={presentation}
        showProgress={true}
        showCounter={true}
        showNavigation={true}
        enableKeyboard={true}
        defaultTransition="slide"
        onBackToLibrary={handleBackToLibrary}
      />
    </div>
  )
}
